import 'react-phone-number-input/style.css';

import { ReactNode, useId, useState } from 'react';
import PhoneInput, { type Country, type Value } from 'react-phone-number-input';

import { Label as LabelUI } from '@/components/ui/label';

import { cn } from '@/lib/utils';

type PhoneNumberInputProps = {
  value?: Value | string;
  onChange?: (value: Value | undefined) => void;
  label?: ReactNode;
  isInvalid?: boolean;
  disabled?: boolean;
  defaultCountry?: Country;
  id?: string;
  className?: string;
};

const DEFAULT_COUNTRY: Country = 'UA';

const PhoneNumberInput = (props: PhoneNumberInputProps) => {
  const {
    value,
    onChange,
    label,
    isInvalid,
    disabled,
    defaultCountry = DEFAULT_COUNTRY,
    id,
    className,
  } = props;

  const generatedId = useId();
  const inputId = id ?? generatedId;

  const [isFocused, setIsFocused] = useState(false);

  const isFloating = isFocused || Boolean(value);

  return (
    <div className="relative">
      <PhoneInput
        id={inputId}
        value={value as Value | undefined}
        onChange={(newValue) => onChange?.(newValue)}
        defaultCountry={defaultCountry}
        international
        countryCallingCodeEditable={false}
        disabled={disabled}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={cn(
          'phone-input',
          'flex h-10 w-full rounded-md border-2 border-transparent bg-transparent px-3 py-2 text-base',
          'transition-colors',
          'focus-within:border-transparent',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      />
      {label && (
        <>
          <fieldset
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 top-[-5px] overflow-hidden rounded-md bg-transparent px-[9px] transition-colors',
              isFocused && !isInvalid && 'border-primary border-2',
              !isFocused && !isInvalid && 'border-input border-2',
              isInvalid && 'border-destructive border-2',
            )}
          >
            <legend
              className={cn(
                'invisible block h-[11px] w-auto overflow-hidden p-0 text-xs whitespace-nowrap',
                isFloating
                  ? 'max-w-full transition-[max-width] delay-50 duration-100 ease-out'
                  : 'max-w-[0.01px] transition-[max-width] duration-50 ease-out',
              )}
            >
              <span className="visible inline-block px-1 opacity-0">
                {label}
              </span>
            </legend>
          </fieldset>
          <LabelUI
            htmlFor={inputId}
            className={cn(
              'text-muted-foreground pointer-events-none absolute gap-1 transition-all duration-200 ease-out',
              isFloating
                ? 'top-0 left-[13px] -translate-y-1/2 text-xs'
                : 'top-1/2 left-24 -translate-y-1/2 text-sm',
              isInvalid ? 'text-destructive' : isFocused && 'text-primary',
            )}
          >
            {label}
          </LabelUI>
        </>
      )}
    </div>
  );
};

export { PhoneNumberInput };
export type { PhoneNumberInputProps };
